import { type UseQueryResult, useQuery, keepPreviousData } from "@tanstack/react-query";
import { ColumnSort } from "@tanstack/react-table";
import { AxiosResponse } from "axios";
import { format } from "date-fns";
import { type PollingPolicy } from "@/lib/polling";
import { type DateRange } from "react-day-picker";
import {
  type ApiPaginatedResponse,
  ApiResponse,
  type Files,
  apiClient,
  createFormData,
  unwrapOrThrow,
  useInfiniteQuery,
} from "./_client";
import { downloadBlob } from "@/lib/utils";
import { type Portfolio } from "./portfolios";
import { type Analysis } from "./analyses";
import { type CloudStorageProvider } from "./integrations";
import { type OutputFormat } from "./deep-dive-personas";

export interface ProjectDocument {
  id: string;
  filename: string;
  doc_type: string;
  category_drilldown: string;
  created_at: string;
  created_by: string;
}

export interface Project {
  id: string;
  editable?: boolean;
  name: string;
  created_at?: string;
  last_modified?: string;
  has_portfolio?: boolean;
  created_by?: string;
  organization?: string;
  document_count?: number;
  integration_count?: number;
  portfolios?: Portfolio[];
  is_archived?: boolean;
  async_question_answering_task_datetime?: string;
  integration_providers?: CloudStorageProvider[];
  graph_rag_ontology?: "renewable" | "bankruptcy" | "generic";
  dynamic_fields: Record<string, string | number | boolean | Date>;
  // Provenance for auto-filled (extracted) fields, keyed by field name.
  field_citations?: Record<string, FieldCitation>;
}

export interface FieldCitation {
  text: string;
  page?: number | null;
  document_id?: string | null;
  filename?: string | null;
  report_id?: string | null;
  answer_id?: string | null;
}

// Human-readable source label for a field citation (e.g. "PPA.pdf, p.3"),
// or null when the citation has no document (report-level only).
export const citationLabel = (citation?: FieldCitation | null): string | null =>
  citation?.filename
    ? `${citation.filename}${citation.page != null ? `, p.${citation.page}` : ""}`
    : null;

interface PortfolioProject {
  id: string;
  project: Project;
  created_at: string;
  updated_at: string;
  editable: boolean;
}
type PortfolioProjectPaginatedResponse = ApiPaginatedResponse<PortfolioProject>;

export type ProjectPaginatedResponse = ApiPaginatedResponse<Project>;

export const projectFieldSectionOrder = [
  "Basic Information",
  "Project Description",
  "Project Identification",
  "Bonus Credits",
  "Tax Credit Value",
  "Other",
];

// Left this here in case it is still used in pending PRs
export const useProjects = (
  pageSize: number = 10,
  pageIndex: number = 1,
  filter: string,
  ordering: ColumnSort | string,
  showArchived: boolean = false,
  showHasPortfolio: boolean = false,
): UseQueryResult<ProjectPaginatedResponse> => {
  let url = `api/projects/?page_size=${pageSize}&page=${pageIndex}`;
  if (filter) {
    url += `&search=${filter}`;
  }
  if (ordering) {
    if (typeof ordering === "string") {
      url += `&ordering=${ordering}`;
    } else {
      url += `&ordering=${ordering.desc ? "-" : ""}${ordering.id}`;
    }
  }
  if (showArchived) {
    url += `&show_archived=true`;
  }
  const query = useQuery<ProjectPaginatedResponse>({
    queryKey: [
      "projects",
      pageSize,
      pageIndex,
      filter,
      ordering,
      showHasPortfolio,
      showArchived,
    ],
    queryFn: async () => {
      const response = await apiClient.get<
        never,
        AxiosResponse<ProjectPaginatedResponse>
      >(url + `&hide_has_portfolio=${showHasPortfolio}`);
      return response.data;
    },
    staleTime: 30 * 1000, // 30 seconds
    placeholderData: keepPreviousData,
  });
  return query;
};

export const useAllProjects = ({
  fullAccessOnly = false,
}: {
  fullAccessOnly?: boolean;
} = {}) => {
  return useQuery<Project[], Error>({
    queryKey: ["all-projects", fullAccessOnly],
    queryFn: async () => {
      const all: Project[] = [];
      let next: string | null = "api/projects/?page=1";
      if (fullAccessOnly) {
        next += "&full_access=true";
      }

      while (next) {
        const resp: AxiosResponse<ApiPaginatedResponse<Project>> =
          await apiClient.get<
            never,
            AxiosResponse<ApiPaginatedResponse<Project>>
          >(next);

        all.push(...resp.data.results);
        next = resp.data.next;
      }

      return all;
    },
    staleTime: 60 * 1000,
  });
};

interface UseProjectsInfiniteQueryProps {
  pageSize?: number;
  pageIndex?: number;
  filter?: string;
  ordering?: ColumnSort | string;
  showHasPortfolio?: boolean;
  dateRangeFilter?: DateRange;
}

export const useProjectsInfiniteQuery = ({
  pageSize = 10,
  pageIndex = 1,
  filter,
  ordering,
  showHasPortfolio = false,
  dateRangeFilter,
}: UseProjectsInfiniteQueryProps = {}) => {
  let url = `api/projects/?page_size=${pageSize}&page=${pageIndex}`;
  if (filter) {
    url += `&search=${filter}`;
  }
  if (ordering) {
    if (typeof ordering === "string") {
      url += `&ordering=${ordering}`;
    } else {
      url += `&ordering=${ordering.desc ? "-" : ""}${ordering.id}`;
    }
  }
  if (dateRangeFilter?.from) {
    url += `&target_pis__gte=${format(dateRangeFilter.from, "yyyy-MM-dd")}`;
  }
  if (dateRangeFilter?.to) {
    url += `&target_pis__lte=${format(dateRangeFilter.to, "yyyy-MM-dd")}`;
  }
  const query = useInfiniteQuery<Project>({
    queryKey: [
      "projects",
      pageSize,
      pageIndex,
      filter,
      ordering,
      showHasPortfolio,
      dateRangeFilter?.from,
      dateRangeFilter?.to,
    ],
    queryFn: async () => {
      const response = await apiClient.get<
        never,
        AxiosResponse<ProjectPaginatedResponse>
      >(url + `&hide_has_portfolio=${showHasPortfolio}`);
      return response.data;
    },
    staleTime: 30 * 1000, // 30 seconds
    initialPageParam: 1,
    getPreviousPageParam: (firstPage) => {
      return firstPage?.previous ?? undefined;
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.next ?? undefined;
    },
  });
  return query;
};

export const singleProjectKey = (projectId?: string) =>
  ["singleProject", projectId] as const;

export const useSingleProject = (
  projectId?: string,
  polling?: PollingPolicy,
): UseQueryResult<Project> => {
  const query = useQuery<Project>({
    queryKey: singleProjectKey(projectId),
    queryFn: async () => {
      const response = await apiClient.get<never, AxiosResponse<Project>>(
        `api/projects/${projectId}/`,
      );
      return response.data;
    },
    staleTime: 30 * 1000, // 30 seconds
    enabled: !!projectId,
    ...polling,
  });
  return query;
};

export const getOneProject = async (id: string) => {
  const response = await apiClient.get<never, AxiosResponse<Project>>(
    `api/projects/${id}/`,
  );
  return response.data;
};

export const archiveProject = async (
  projectId: string,
): Promise<ApiResponse> => {
  const response = await apiClient.post<never, ApiResponse>(
    `api/projects/${projectId}/archive/`,
  );
  return response;
};

export const unarchiveProject = async (
  projectId: string,
): Promise<ApiResponse> => {
  const response = await apiClient.post<never, ApiResponse>(
    `api/projects/${projectId}/unarchive/`,
  );
  return response;
};

export type ProjectGraphUpdateResponse = {
  status:
    | "up_to_date"
    | "processing"
    | "ready"
    | "queued"
    | "running"
    | "failed";
  can_request: boolean;
  ungraphed_document_count?: number;
  graph_documents_done?: number;
  graph_documents_total?: number;
};

export const projectGraphUpdateQueryKey = (projectId: string) => [
  "project-graph-update",
  projectId,
];

export const useProjectGraphUpdateStatus = (
  projectId: string,
  enabled: boolean,
) =>
  useQuery<ProjectGraphUpdateResponse>({
    queryKey: projectGraphUpdateQueryKey(projectId),
    queryFn: async () =>
      unwrapOrThrow<ProjectGraphUpdateResponse>(
        await apiClient.get(`api/projects/${projectId}/graph-update/`),
      ),
    enabled,
    refetchInterval: (query) =>
      query.state.data?.status === "queued" ||
      query.state.data?.status === "running" ||
      query.state.data?.status === "processing"
        ? 5000
        : false,
  });

export const requestProjectGraphUpdate = async (
  projectId: string,
): Promise<ProjectGraphUpdateResponse> =>
  unwrapOrThrow<ProjectGraphUpdateResponse>(
    await apiClient.post(`api/projects/${projectId}/graph-update/`),
  );

// portfolio projects
export const usePortfolioProjects = ({
  portfolioId,
  pageSize = 10,
}: {
  portfolioId?: string;
  pageSize?: number;
}) => {
  const query = useInfiniteQuery<PortfolioProject>({
    queryKey: ["portfolio-projects", portfolioId, pageSize], //, filter, ordering],
    queryFn: async ({ pageParam }: { pageParam: string | number }) => {
      const response = await apiClient.get<
        never,
        AxiosResponse<PortfolioProjectPaginatedResponse>
      >(
        typeof pageParam === "number"
          ? `api/portfolios/${portfolioId}/projects/?page_size=${pageSize}&page=${pageParam}`
          : pageParam,
      );
      return response.data;
    },
    initialPageParam: 1,
    getPreviousPageParam: (firstPage) => {
      return firstPage?.previous ?? undefined;
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.next ?? undefined;
    },
  });

  if (!portfolioId) {
    return {
      data: {
        pages: [],
      },
      fetchNextPage: () => {},
      fetchPreviousPage: () => {},
      hasNextPage: false,
      hasPreviousPage: false,
      isFetching: false,
      isFetchingNextPage: false,
      isFetchingPreviousPage: false,
      isLoading: false,
      status: "success",
    };
  }
  return query;
};

type ProjectCreateResponse = ApiResponse<Project>;

const editProjectAPI = (method: "patch" | "post") => {
  return async (
    project: Partial<Project> & { portfolio_id?: string; documents?: Files },
  ): Promise<ProjectCreateResponse> => {
  const url = project.id ? `api/projects/${project.id}/` : "api/projects/";
  if (project.documents?.length) {
    const form = createFormData(project);
    const options = {
      method,
      url,
      headers: {
        "Content-Type": "multipart/form-data",
      },
      data: form,
    };

    const response = await apiClient.request<never, ProjectCreateResponse>(
      options,
    );
    // .then(function (response) {
    //   console.log(response.data);
    // })
    // .catch(function (error) {
    //   console.error(error);
    // });
    return response;
  } else {
    const response = await apiClient[method]<never, ProjectCreateResponse>(
      url,
      project,
    );
    return response;
  }
  };
};

export const createProject = editProjectAPI("post");
export const patchProject = editProjectAPI("patch");

export const deletePortfolioProject = async ({
  portfolioId,
  portfolioProjectId,
}: {
  portfolioId: string;
  portfolioProjectId: string;
}): Promise<ApiResponse> => {
  const response = await apiClient.delete<never, ApiResponse>(
    `api/portfolios/${portfolioId}/projects/${portfolioProjectId}/`,
  );
  return response;
};

// Project fields
export type ProjectFieldType =
  | "text"
  | "number"
  | "boolean"
  | "date"
  | "currency";
export type ProjectFieldCategoryType =
  | "Basic Information"
  | "Bonus Credits"
  | "Project Description"
  | "Tax Credit Value"
  | "Project Identification";

export interface ProjectField {
  id: string;
  organization: string;
  name: string;
  type: ProjectFieldType;
  options?: string[];
  conditional_options?: Record<string, Record<string, string[]>>;
  field_category: ProjectFieldCategoryType;
  required: boolean;
  is_identifier: boolean;
}

export const useAllProjectFields = () => {
  return useQuery<ProjectField[], Error>({
    queryKey: ["project-fields"],
    queryFn: async () => {
      const all: ProjectField[] = [];
      let next: string | null = "api/projects/fields/?page=1";

      while (next) {
        const resp: AxiosResponse<ApiPaginatedResponse<ProjectField>> =
          await apiClient.get<
            never,
            AxiosResponse<ApiPaginatedResponse<ProjectField>>
          >(next);

        all.push(...resp.data.results);
        next = resp.data.next;
      }

      return all;
    },
    staleTime: 10 * 60 * 1000,
  });
};

export const createDefaultProjectFields = async (): Promise<
  ApiResponse<ProjectField[]>
> => {
  const response = await apiClient.post<never, ApiResponse<ProjectField[]>>(
    `api/projects/fields/generate-default/`,
  );
  return response;
};

export const createProjectField = async (
  field: Partial<ProjectField>,
): Promise<ApiResponse<ProjectField>> => {
  const response = await apiClient.post<never, ApiResponse<ProjectField>>(
    "api/projects/fields/",
    field,
  );
  return response;
};

export const updateProjectField = async (
  field: Partial<ProjectField>,
): Promise<ApiResponse<ProjectField>> => {
  const response = await apiClient.patch<never, ApiResponse<ProjectField>>(
    `api/projects/fields/${field.id}/`,
    field,
  );
  return response;
};

export const deleteProjectField = async ({
  id,
}: {
  id: string;
}): Promise<ApiResponse> => {
  const response = await apiClient.delete<never, ApiResponse>(
    `api/projects/fields/${id}/`,
  );
  return response;
};

// BULK PROJECT UPLOADS
export type BulkProjectDetectFieldsResponse = {
  detected_fields: string[];
  new_fields: {
    field_name: string;
    field_type: ProjectFieldType;
    text_type?: "free_input" | "option_choices";
  }[];
  matched_fields: Record<string, string>;
  updated_existing_fields_with_options: [string, string[]][];
  updated_existing_conditional_fields: Record<string, Record<string, string[]>>;
};

// Step 1: detect fields
export const bulkUploadProjectDetectFields = async ({
  file,
  portfolioId,
}: {
  file: File;
  portfolioId?: string;
}): Promise<ApiResponse<BulkProjectDetectFieldsResponse, { bulkfile: string }>> => {
  const form = new FormData();
  form.append("bulkfile", file);
  form.append("step", "detect_fields");
  if (portfolioId) form.append("portfolio_id", portfolioId);
  const options = {
    method: "POST",
    url: "api/projects/bulk/",
    headers: {
      "Content-Type": "multipart/form-data",
    },
    data: form,
  };
  const response = await apiClient.request<
    never,
    ApiResponse<BulkProjectDetectFieldsResponse, { bulkfile: string }>
  >(options);
  return response;
};

type BulkProjectImportDataResponse = ApiResponse<
  {
    created_projects: Project[];
    updated_projects: Project[];
  },
  { bulkfile: string }
>;

// Step 2: import data
export const bulkUploadProjectImportData = async ({
  file,
  portfolioId,
  fieldMappings,
  updated_existing_fields_with_options,
  updated_existing_conditional_fields,
}: {
  file: File;
  portfolioId?: string;
  fieldMappings?: {
    file_header?: string;
    import_as?: string;
    type?: string;
    is_identifier?: boolean;
    has_options?: boolean;
  }[];
  updated_existing_fields_with_options?: [string, string[]][];
  updated_existing_conditional_fields?: Record<string, Record<string, string[]>>;
}): Promise<BulkProjectImportDataResponse> => {
  const form = new FormData();
  form.append("bulkfile", file);
  form.append("step", "import_data");
  if (portfolioId) form.append("portfolio_id", portfolioId);
  if (fieldMappings) {
    fieldMappings.forEach((field) => {
      form.append("field_mappings", JSON.stringify(field));
    });
  }
  if (updated_existing_fields_with_options) {
    updated_existing_fields_with_options.forEach((field) => {
      form.append(
        "updated_existing_fields_with_options",
        JSON.stringify(field),
      );
    });
  }
  if (updated_existing_conditional_fields) {
    form.append(
      "updated_existing_conditional_fields",
      JSON.stringify(updated_existing_conditional_fields),
    );
  }
  const options = {
    method: "POST",
    url: "api/projects/bulk/",
    headers: {
      "Content-Type": "multipart/form-data",
    },
    data: form,
  };
  const response = await apiClient.request<
    never,
    BulkProjectImportDataResponse
  >(options);
  return response;
};

export const deleteProject = async (id: string): Promise<ApiResponse> => {
  const response = await apiClient.delete<never, ApiResponse>(
    `api/projects/${id}/`,
  );
  return response;
};

export const bulkArchiveProjects = async (
  ids: string[],
): Promise<{ archived_count: number; errors: Array<{ id: string; error: string }> }> => {
  const response = await apiClient.post("api/projects/bulk-archive/", { ids });
  return response.data;
};

export const bulkDeleteProjects = async (
  ids: string[],
): Promise<{ deleted_count: number; errors: Array<{ id: string; error: string }> }> => {
  const response = await apiClient.post("api/projects/bulk-delete/", { ids });
  return response.data;
};

export const exportProjectsXlsx = async () => {
  const url = `api/projects/export/`;
  const response = await apiClient.get(url, {
    responseType: "blob",
  });

  if (response.data) {
    downloadBlob(
      response.data,
      "projects.xlsx",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    );
  }

  return response;
};

// Project AI Summary
export interface ProjectSummaryData {
  id: string;
  content: string;
  citations: Array<{
    text: string;
    source: string;
    page?: number;
  }>;
  source_report: string | null;
  generated_at: string;
  generated_by: string | null;
}

export const useProjectSummary = (projectId: string) =>
  useQuery<ProjectSummaryData | null>({
    queryKey: ["project-summary", projectId],
    queryFn: async () => {
      const response = await apiClient.get<
        never,
        AxiosResponse<ProjectSummaryData | null>
      >(`api/projects/${projectId}/ai-summary/`);
      // interceptor error envelope has no `data`; null = "no summary yet"
      return response.data ?? null;
    },
    staleTime: 60 * 1000,
  });

// Analysis Templates
export interface AnalysisTemplate {
  id: string;
  title: string;
  description: string;
  section_count: number;
  question_count: number;
  output_summary: string;
  template_summary: string;
  questions_preview: string[];
}

export const useAvailableTemplates = (projectId: string) =>
  useQuery<AnalysisTemplate[]>({
    queryKey: ["available-templates", projectId],
    queryFn: async () => {
      const response = await apiClient.get<
        never,
        AxiosResponse<AnalysisTemplate[]>
      >(`api/projects/${projectId}/available-templates/`);
      return response.data;
    },
    staleTime: 60 * 1000,
  });

export const applyAnalysisToProject = async (
  projectId: string,
  analysisId: string,
  options?: { run?: boolean },
): Promise<{ analysis_id: string; report_id: string | null; detail?: string; analysis?: Analysis }> => {
  const response = await apiClient.post<
    never,
    AxiosResponse<{ analysis_id: string; report_id: string | null; detail?: string; analysis?: Analysis }>
  >(`api/projects/${projectId}/apply-analysis/`, {
    analysis_id: analysisId,
    ...(options?.run === false && { run: false }),
  });
  return response.data;
};

export const useAvailablePortfolioTemplates = (portfolioId: string) =>
  useQuery<AnalysisTemplate[]>({
    queryKey: ["available-templates", "portfolio", portfolioId],
    queryFn: async () => {
      const response = await apiClient.get<
        never,
        AxiosResponse<AnalysisTemplate[]>
      >(`api/portfolios/${portfolioId}/available-templates/`);
      return response.data;
    },
    staleTime: 60 * 1000,
  });

export const applyAnalysisToPortfolio = async (
  portfolioId: string,
  analysisId: string,
): Promise<{ analysis_id: string; report_id: string | null; detail?: string }> => {
  const response = await apiClient.post<
    never,
    AxiosResponse<{ analysis_id: string; report_id: string | null; detail?: string }>
  >(`api/portfolios/${portfolioId}/apply-analysis/`, {
    analysis_id: analysisId,
  });
  return response.data;
};

export const createProjectReport = async (
  projectId: string,
  data?: { title?: string },
): Promise<Analysis> => {
  const response = await apiClient.post<never, AxiosResponse<Analysis>>(
    `api/projects/${projectId}/create-report/`,
    data ?? {},
  );
  return response.data;
};

export const createPortfolioReport = async (
  portfolioId: string,
  data?: { title?: string },
): Promise<Analysis> => {
  const response = await apiClient.post<never, AxiosResponse<Analysis>>(
    `api/portfolios/${portfolioId}/create-report/`,
    data ?? {},
  );
  return response.data;
};

// Deep Dive

export interface DeepDiveAnswerInsight {
  question: string;
  variable_name: string;
  value: string | null;
  insight: "complete" | "incomplete" | "consider_asking" | "risk";
  insight_reason: string | null;
  footnote_indices: number[];
}

export interface DeepDiveFootnote {
  index: number;
  document_name: string;
  page: number | null;
  document_id: string | null;
  answer_group_id: string | null;
}

export interface DeepDiveSection {
  title: string;
  section_id: string;
  narrative: string;
  answers: DeepDiveAnswerInsight[];
  footnotes: DeepDiveFootnote[];
}

export type ChecklistResultStatus =
  | "covered"
  | "partial"
  | "missing"
  | "unassigned";

export interface ChecklistResult {
  label: string;
  /**
   * The persona checklist item's server-assigned id — the canonical join
   * key to `criteria_compilation` (label is the legacy fallback). Absent
   * on dives generated before COM-1518.
   */
  criterion_id?: string | null;
  /**
   * True if the persona flagged this as a required check. Required items
   * with a non-covered status should be visually prioritized in the UI.
   * Absent on legacy payloads — default to true (matches serializer).
   */
  required?: boolean;
  covered: boolean;
  /**
   * Coverage status. Always present on new payloads; older deep dives may
   * omit it, in which case fall back to the `covered` boolean. `partial`
   * means the item's central check is met but some sub-criteria from its
   * evaluation_criteria are missing; see prose for the specific gaps.
   */
  status?: ChecklistResultStatus;
  covered_in?: string[];
  /**
   * Sections that scored the item as `partial` — central check met but
   * sub-criteria gaps. Only populated for new (three-way) payloads.
   */
  partial_in?: string[];
  not_covered_in?: string[];
  /**
   * Historically the first section that covered the item (legacy). New
   * payloads set this to the owner section id when one is assigned. Prefer
   * `owner_section_id` for attribution.
   */
  section_id: string | null;
  /**
   * Section id primarily responsible for evaluating this item. `null` means
   * the checklist-assignment pass couldn't route the item to any section.
   */
  owner_section_id?: string | null;
  /**
   * Additional section ids where a cross-cutting item is also scored.
   */
  cross_cutting_section_ids?: string[];
  excerpt: string | null;
  /**
   * True when a cross-cutting item's owner section produced no verdict
   * (e.g. its analysis was skipped) — the item is capped at partial on
   * secondary evidence alone (COM-1515).
   */
  owner_unscored?: boolean;
}

export interface ChecklistAssignment {
  item_label: string;
  owner_section_id: string | null;
  cross_cutting_section_ids: string[];
  cross_cutting: boolean;
  rationale?: string | null;
}

// ---- COM-1510 two-stage artifact (schema_version 2) — all optional so
// legacy dives keep rendering exactly as before. ----

export type FindingType =
  | "red_flag"
  | "gap"
  | "inconsistency"
  | "positive"
  | "observation";
export type FindingSeverity = "critical" | "high" | "medium" | "low";
export type FindingStatus = "confirmed" | "potential" | "missing_data";

export interface DeepDiveFinding {
  id: string;
  /** Deterministic fingerprint — survives regeneration (feedback keys on it). */
  stable_key: string;
  section_ids: string[];
  scope: "section" | "cross_section";
  origin: "writer" | "verification" | "synthesis";
  type: FindingType;
  severity: FindingSeverity | null;
  status: FindingStatus;
  title: string;
  fact: {
    text: string;
    answer_variables: string[];
    /** Stored empty by design — provenance links go via the Facts Reviewed rows. */
    footnote_indices: number[];
  };
  criterion_id: string | null;
  /** Labeled analytical judgment — no citation requirement. */
  implication: string;
  rfi: string;
  missing_variables: string[];
  grounded?: boolean;
  merged_finding_ids?: string[];
}

export type VerdictRating =
  | "proceed"
  | "proceed_with_conditions"
  | "material_follow_up"
  | "do_not_proceed"
  | "insufficient_information";

export interface DeepDiveVerdict {
  rating: VerdictRating;
  /** Pre-clamp model rating; null on the deterministic fallback path. */
  llm_rating: VerdictRating | null;
  summary: string;
  rationale_finding_ids: string[];
  gating_finding_ids: string[];
  prioritized_rfis: { finding_id: string; request: string }[];
  counts: {
    by_type: Partial<Record<FindingType, number>>;
    by_severity: Partial<Record<FindingSeverity, number>>;
  };
  caveats: string[];
  /** Render-scoped QA notes — cleared on format switch. */
  render_caveats: string[];
  rating_floor: VerdictRating;
  rating_floor_reason: string;
  rating_cap: VerdictRating;
  rating_cap_reason: string;
  insufficient_information_triggered: boolean;
  insufficient_information_reason: string;
  fallback_used: boolean;
  synthesis_prompt_version: string;
  generated_at: string;
}

export interface DeepDiveActiveRender {
  format: OutputFormatValue;
  rendered_at: string;
  selected_finding_ids: Record<string, string[]>;
  render_prompt_version: string;
  /** Sections that kept their previous format after a failed rerender. */
  stale_section_ids?: string[];
}

// The canonical union lives in ./deep-dive-personas (the documented home
// of the output-format vocabulary); this alias keeps dive-side naming.
export type OutputFormatValue = OutputFormat;

export interface DeepDiveContent {
  /** Absent = legacy single-stage dive; 2 = two-stage artifact. */
  schema_version?: number;
  sections: DeepDiveSection[];
  metadata: {
    report_id: string;
    report_title: string;
    project_name: string;
    generated_at: string;
    section_count: number;
    total_footnotes: number;
    /** Sections that failed generation even after retry — absent from sections. */
    skipped_sections?: number;
    skipped_section_details?: { section_id: string; title: string }[];
    /** Persona extracted-direction routing outcome for this run. */
    direction_summary?: { total: number; unassigned_titles: string[] };
    sections_included?: number;
  };
  checklist_results?: ChecklistResult[];
  checklist_assignments?: ChecklistAssignment[];
  findings?: DeepDiveFinding[];
  verdict?: DeepDiveVerdict;
  active_render?: DeepDiveActiveRender;
  criteria_compilation?: CriteriaCompilationEntry[];
}

/** Per-criterion evidence resolution (COM-1513); label added for the
 * legacy UI join in COM-1518 — older v2 dives may lack it. */
export interface CriteriaCompilationEntry {
  criterion_id: string | null;
  label?: string;
  status: "resolved" | "unresolved" | "not_applicable";
  resolved_question_ids: string[];
}

export type AnswerFilter = "all" | "validated" | "not_incorrect";

export interface DeepDive {
  id: string;
  title: string;
  content: DeepDiveContent;
  status: "pending" | "running" | "completed" | "failed";
  source_report: string | null;
  persona: string | null;
  persona_name: string | null;
  user_instructions: string;
  section_ids: string[];
  answer_filter: AnswerFilter;
  error_message: string;
  /** Verdict is absent because synthesis errored, not because none applies. */
  synthesis_failed: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface DeepDiveListItem {
  id: string;
  title: string;
  status: "pending" | "running" | "completed" | "failed";
  source_report: string | null;
  source_report_title: string | null;
  persona: string | null;
  persona_name: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  error_message: string;
  /** Verdict is absent because synthesis errored, not because none applies. */
  synthesis_failed: boolean;
}

export const deepDiveKeys = {
  all: (projectId: string) => ["deep-dives", projectId] as const,
  detail: (projectId: string, ddId: string) =>
    ["deep-dives", projectId, ddId] as const,
};

export const useDeepDives = (projectId: string) =>
  useQuery<ApiPaginatedResponse<DeepDiveListItem>>({
    queryKey: deepDiveKeys.all(projectId),
    queryFn: async () => {
      const response = await apiClient.get<
        never,
        AxiosResponse<ApiPaginatedResponse<DeepDiveListItem>>
      >(`api/projects/${projectId}/deep-dives/`);
      return response.data;
    },
    staleTime: 30 * 1000,
  });

export const fetchDeepDive = async (
  projectId: string,
  deepDiveId: string,
): Promise<DeepDive> => {
  const response = await apiClient.get<never, AxiosResponse<DeepDive>>(
    `api/projects/${projectId}/deep-dives/${deepDiveId}/`,
  );
  return response.data;
};

export const useDeepDive = (
  projectId: string,
  deepDiveId: string | null,
  { poll = false }: { poll?: boolean } = {},
) =>
  useQuery<DeepDive>({
    queryKey: deepDiveKeys.detail(projectId, deepDiveId ?? ""),
    queryFn: () => fetchDeepDive(projectId, deepDiveId!),
    enabled: !!deepDiveId,
    refetchInterval: poll
      ? (query) => {
          // Exponential backoff: 2s, 4s, 8s, capped at 15s
          const count = query.state.dataUpdateCount;
          return Math.min(2000 * Math.pow(2, count), 15000);
        }
      : false,
    staleTime: 10 * 1000,
  });

export const generateDeepDive = async (
  projectId: string,
  reportId?: string,
  instructions?: string,
  sectionIds?: string[],
  personaId?: string,
  answerFilter?: AnswerFilter,
): Promise<DeepDive> => {
  const response = await apiClient.post<never, AxiosResponse<DeepDive>>(
    `api/projects/${projectId}/generate-deep-dive/`,
    {
      ...(reportId && { report_id: reportId }),
      ...(instructions && { instructions }),
      ...(sectionIds?.length && { section_ids: sectionIds }),
      ...(personaId && { persona_id: personaId }),
      ...(answerFilter && answerFilter !== "all"
        ? { answer_filter: answerFilter }
        : {}),
    },
  );
  return response.data;
};

export const deleteDeepDive = async (
  projectId: string,
  deepDiveId: string,
): Promise<void> => {
  await apiClient.delete(
    `api/projects/${projectId}/deep-dives/${deepDiveId}/`,
  );
};

export const exportDeepDiveDocx = async (
  projectId: string,
  deepDiveId: string,
): Promise<void> => {
  const response = await apiClient.get(
    `api/projects/${projectId}/deep-dives/${deepDiveId}/export-docx/`,
    { responseType: "blob" },
  );
  const disposition = String(response.headers["content-disposition"] ?? "");
  const match = disposition.match(/filename="?([^";\n]+)"?/);
  const filename = match?.[1] ?? "deep-dive.docx";
  downloadBlob(
    response.data,
    filename,
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  );
};

/**
 * Kick off a render-only format switch (COM-1514). Returns 202 — the task
 * runs in the background; callers poll the dive until
 * `content.active_render.format` matches. Only valid for schema_version 2
 * dives.
 */
export const rerenderDeepDive = async (
  projectId: string,
  deepDiveId: string,
  outputFormat: OutputFormatValue,
): Promise<{ status: string; run_id: string }> => {
  const response = await apiClient.post(
    `api/projects/${projectId}/deep-dives/${deepDiveId}/rerender/`,
    { output_format: outputFormat },
  );
  return response.data;
};

export interface DiffSummaryResponse {
  id: string;
  summary: string;
  status: "pending" | "running" | "completed" | "failed";
  error_message: string;
  deep_dive_ids: string[];
  created_at: string;
}

export const createDiffSummary = async (
  projectId: string,
  deepDiveIds: string[],
): Promise<DiffSummaryResponse> => {
  const response = await apiClient.post<
    never,
    AxiosResponse<DiffSummaryResponse>
  >(
    `api/projects/${projectId}/deep-dives/summarize-diff/`,
    { deep_dive_ids: deepDiveIds },
  );
  return response.data;
};

export const getDiffSummary = async (
  projectId: string,
  diffId: string,
): Promise<DiffSummaryResponse> => {
  const response = await apiClient.get<
    never,
    AxiosResponse<DiffSummaryResponse>
  >(`api/projects/${projectId}/deep-dives/diff-summary/${diffId}/`);
  return response.data;
};

export interface ReportSection {
  id: string;
  title: string;
}

export interface AvailableReport {
  id: string;
  title: string;
  analysis_title: string;
  analysis_is_template?: boolean;
  source_template_title?: string | null;
}

export interface ReportSectionsResponse {
  report_id: string | null;
  report_title: string | null;
  analysis_title: string | null;
  sections: ReportSection[];
  available_reports: AvailableReport[];
}

export const useReportSections = (projectId: string, reportId?: string) =>
  useQuery<ReportSectionsResponse>({
    queryKey: ["report-sections", projectId, reportId ?? "latest"],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (reportId) searchParams.set("report_id", reportId);
      const qs = searchParams.toString() ? `?${searchParams.toString()}` : "";
      const response = await apiClient.get<
        never,
        AxiosResponse<ReportSectionsResponse>
      >(`api/projects/${projectId}/report-sections/${qs}`);
      return response.data;
    },
    staleTime: 60 * 1000,
  });

export const clearProjectSummary = async (
  projectId: string,
): Promise<void> => {
  await apiClient.delete(`api/projects/${projectId}/clear-summary/`);
};

export const generateProjectSummary = async (
  projectId: string,
  reportId?: string,
  prompt?: string,
): Promise<ProjectSummaryData> => {
  const response = await apiClient.post<
    never,
    AxiosResponse<ProjectSummaryData>
  >(
    `api/projects/${projectId}/generate-summary/`,
    {
      ...(reportId && { report_id: reportId }),
      ...(prompt && { prompt }),
    },
  );
  return response.data;
};
